import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, User, Bot, Headset, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUserAuth } from "../context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "dj_chat_sid";

export const ChatWidget = () => {
  const { t } = useTranslation();
  const { user } = useUserAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);  // {role, content, at}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SID_KEY) || "");
  const [handoffSent, setHandoffSent] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffForm, setHandoffForm] = useState({
    first_name: "",
    last_name: "",
    email: user?.email || "",
    phone: "",
  });
  const [handoffError, setHandoffError] = useState("");
  const [handoffBusy, setHandoffBusy] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Rehydrate history when opening for the first time and we already have a sid
  useEffect(() => {
    if (!open || messages.length > 0 || !sessionId) return;
    axios.get(`${API}/chat/history/${sessionId}`).then((r) => {
      setMessages(r.data.messages || []);
      setHandoffSent(Boolean(r.data.handoff_requested));
    }).catch(() => {});
  }, [open, sessionId, messages.length]);

  // Autoscroll to bottom on new message
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, open, sending]);

  // Focus input each time the widget opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  // Allow other pages to open the chat via a global custom event (e.g. Contatti CTA)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("djk:open-chat", handler);
    return () => window.removeEventListener("djk:open-chat", handler);
  }, []);

  const greeting = useMemo(
    () => (user
      ? `Ciao ${user.name?.split(" ")[0] || ""}! Sono Daita-chan ⚡ Come posso aiutarti?`
      : "Ciao! Sono Daita-chan ⚡ Chiedimi qualsiasi cosa su Daita 'n' Jack, prodotti, orari o spedizioni."),
    [user],
  );

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: text, at: now }]);

    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId || undefined,
        message: text,
        user_id: user?.id,
        user_email: user?.email,
        user_name: user?.name,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SID_KEY, data.session_id);
      }
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, at: new Date().toISOString() }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "Mi spiace, non riesco a rispondere ora. Riprova oppure premi \"Parla con un umano\".",
        at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Auto-prefill handoff email/name from logged-in user when available
  useEffect(() => {
    if (!user) return;
    setHandoffForm((f) => ({
      ...f,
      email: f.email || user.email || "",
      first_name: f.first_name || (user.name?.split(" ")[0] || ""),
      last_name: f.last_name || (user.name?.split(" ").slice(1).join(" ") || ""),
    }));
  }, [user]);

  const requestHandoff = async () => {
    setHandoffError("");
    const { first_name, last_name, email, phone } = handoffForm;
    if (!first_name.trim() || !last_name.trim() || !email.trim() || !phone.trim()) {
      setHandoffError("Compila tutti i campi");
      return;
    }
    if (!email.includes("@")) {
      setHandoffError("Email non valida");
      return;
    }
    // Need an existing session — if none, send a tiny first message to create one
    let sid = sessionId;
    if (!sid) {
      try {
        const { data } = await axios.post(`${API}/chat/message`, {
          message: "Vorrei parlare con un operatore.",
          user_id: user?.id,
          user_email: email,
          user_name: `${first_name} ${last_name}`,
        });
        sid = data.session_id;
        setSessionId(sid);
        localStorage.setItem(SID_KEY, sid);
        setMessages((prev) => [
          ...prev,
          { role: "user", content: "Vorrei parlare con un operatore.", at: new Date().toISOString() },
          { role: "assistant", content: data.reply, at: new Date().toISOString() },
        ]);
      } catch {
        setHandoffError("Errore. Riprova.");
        return;
      }
    }
    setHandoffBusy(true);
    try {
      await axios.post(`${API}/chat/handoff`, {
        session_id: sid,
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      setHandoffSent(true);
      setHandoffOpen(false);
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: `Grazie ${first_name}! Silvano o Filippo ti contatteranno presto su ${phone} o ${email} 🙌`,
        at: new Date().toISOString(),
      }]);
    } catch (err) {
      // Stale localStorage session — wipe it, create a fresh one, retry once
      if (err.response?.status === 404) {
        try {
          localStorage.removeItem(SID_KEY);
          const { data } = await axios.post(`${API}/chat/message`, {
            message: "Vorrei parlare con un operatore.",
            user_id: user?.id,
            user_email: email,
            user_name: `${first_name} ${last_name}`,
          });
          const newSid = data.session_id;
          setSessionId(newSid);
          localStorage.setItem(SID_KEY, newSid);
          await axios.post(`${API}/chat/handoff`, {
            session_id: newSid,
            first_name: first_name.trim(),
            last_name: last_name.trim(),
            email: email.trim(),
            phone: phone.trim(),
          });
          setHandoffSent(true);
          setHandoffOpen(false);
          setMessages((prev) => [...prev, {
            role: "assistant",
            content: `Grazie ${first_name}! Silvano o Filippo ti contatteranno presto su ${phone} o ${email} 🙌`,
            at: new Date().toISOString(),
          }]);
          return;
        } catch {/* fall through */}
      }
      setHandoffError(err.response?.data?.detail || "Invio fallito. Riprova.");
    } finally {
      setHandoffBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          aria-label="Apri chat"
          className="fixed bottom-20 sm:bottom-24 right-6 z-[60] flex items-center gap-2 bg-yellow-400 text-black p-4 rounded-full shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] border-2 border-black hover:translate-x-[-2px] hover:translate-y-[-2px] transition group"
        >
          <MessageCircle className="w-6 h-6" strokeWidth={3} />
          <span className="hidden sm:inline font-heading tracking-widest text-sm uppercase pr-1">Chat</span>
          {messages.length === 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-black animate-pulse" />
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 sm:bottom-24 right-6 left-6 sm:left-auto sm:w-[380px] max-h-[min(640px,calc(100vh-7rem))] z-[60] bg-zinc-950 border-2 border-yellow-400 shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] flex flex-col"
        >
          {/* Header */}
          <div className="bg-yellow-400 text-black p-3 flex items-center justify-between border-b-2 border-black">
            <div className="flex items-center gap-2">
              <div className="bg-black text-yellow-400 p-1.5">
                <Bot className="w-4 h-4" strokeWidth={3} />
              </div>
              <div className="leading-none">
                <div className="font-heading uppercase text-sm tracking-widest">Daita-chan</div>
                <div className="font-jp text-[10px] tracking-widest opacity-70">大太チャン</div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              aria-label="Chiudi chat"
              className="bg-black text-yellow-400 p-1.5 border-2 border-black hover:bg-zinc-900"
            >
              <X className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="bg-zinc-900 border-2 border-zinc-800 p-3">
                <div className="flex items-start gap-2">
                  <div className="bg-yellow-400 text-black p-1.5 mt-0.5"><Bot className="w-3 h-3" strokeWidth={3} /></div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{greeting}</p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-heading pl-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Sta scrivendo…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Handoff inline form */}
          {handoffOpen && !handoffSent && (
            <div className="bg-zinc-900 border-t-2 border-yellow-400 p-3 space-y-2" data-testid="handoff-form">
              <div className="text-yellow-400 text-xs font-heading uppercase tracking-widest">
                Lascia i tuoi contatti
              </div>
              <div className="text-zinc-400 text-[11px]">Ti ricontatteremo al più presto.</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  data-testid="handoff-first-name"
                  value={handoffForm.first_name}
                  onChange={(e) => setHandoffForm((f) => ({ ...f, first_name: e.target.value }))}
                  placeholder="Nome *"
                  className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
                />
                <input
                  type="text"
                  data-testid="handoff-last-name"
                  value={handoffForm.last_name}
                  onChange={(e) => setHandoffForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder="Cognome *"
                  className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
                />
              </div>
              <input
                type="email"
                data-testid="handoff-email"
                value={handoffForm.email}
                onChange={(e) => setHandoffForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email *"
                className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
              />
              <input
                type="tel"
                data-testid="handoff-phone"
                value={handoffForm.phone}
                onChange={(e) => setHandoffForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Telefono *"
                className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
              />
              {handoffError && (
                <div className="text-red-400 text-xs font-mono">{handoffError}</div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={requestHandoff}
                  disabled={handoffBusy}
                  data-testid="handoff-confirm"
                  className="flex-1 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs py-2 hover:bg-white transition disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  {handoffBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Invia
                </button>
                <button onClick={() => setHandoffOpen(false)} className="px-3 border-2 border-zinc-800 text-zinc-400 hover:text-yellow-400 text-xs font-heading uppercase tracking-widest">
                  Annulla
                </button>
              </div>
            </div>
          )}

          {/* Composer */}
          <div className="border-t-2 border-zinc-800 bg-zinc-950">
            {/* Handoff bar */}
            {!handoffSent && !handoffOpen && (
              <button
                data-testid="handoff-open"
                onClick={() => setHandoffOpen(true)}
                className="w-full flex items-center justify-center gap-2 text-zinc-400 hover:text-yellow-400 text-[11px] font-heading uppercase tracking-widest py-2 border-b border-zinc-800 transition"
              >
                <Headset className="w-3.5 h-3.5" strokeWidth={3} />
                Parla con un umano
              </button>
            )}
            {handoffSent && (
              <div className="w-full text-center text-yellow-400 text-[11px] font-heading uppercase tracking-widest py-2 border-b border-zinc-800 bg-zinc-900">
                ✓ richiesta inviata
              </div>
            )}

            <div className="p-2 flex gap-2 items-end">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Scrivi un messaggio…"
                className="flex-1 bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white text-sm font-mono px-3 py-2 resize-none outline-none max-h-24"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={sending || !input.trim()}
                aria-label="Invia"
                className="bg-yellow-400 text-black p-3 border-2 border-yellow-400 hover:bg-white disabled:opacity-40 transition"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div data-testid={`bubble-${role}`} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="flex items-start gap-2 max-w-[85%]">
        {!isUser && (
          <div className="bg-yellow-400 text-black p-1.5 flex-shrink-0 mt-0.5">
            <Bot className="w-3 h-3" strokeWidth={3} />
          </div>
        )}
        <div
          className={`px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words border-2 ${
            isUser
              ? "bg-yellow-400 text-black border-yellow-400"
              : "bg-zinc-900 text-zinc-100 border-zinc-800"
          }`}
        >
          {linkify(content)}
        </div>
        {isUser && (
          <div className="bg-zinc-800 text-yellow-400 p-1.5 flex-shrink-0 mt-0.5">
            <User className="w-3 h-3" strokeWidth={3} />
          </div>
        )}
      </div>
    </div>
  );
}

// Turn URLs in chat text into yellow underlined links that open inline (same tab → product page).
const LINK_RE = /(https?:\/\/[^\s)]+|\/product\/[a-zA-Z0-9_-]+)/g;
function linkify(text) {
  if (!text) return null;
  const parts = text.split(LINK_RE);
  return parts.map((part, i) => {
    if (part && LINK_RE.test(part)) {
      // Reset regex internal lastIndex (because the regex uses /g)
      LINK_RE.lastIndex = 0;
      const href = part.startsWith("/") ? part : part;
      return (
        <a
          key={i}
          href={href}
          className="underline font-bold hover:text-yellow-400 break-all"
        >
          {part}
        </a>
      );
    }
    LINK_RE.lastIndex = 0;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export default ChatWidget;
